import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Legenda } from './Legenda';
import { asTijdvakLabel } from './tijdvak';
import type { OverzichtData } from './overzicht.api';

/**
 * Wat er per maand (of per dag) is gezegd, niet opgeteld — de tegenhanger van de
 * opgetelde grafiek eronder. Hier mag een tijdvak lager zijn dan het vorige, en
 * dat is precies het punt: zo zie je wanneer iets opliep en wanneer het stilviel.
 *
 * Staven en geen vlakken: dit zijn losse tellingen per tijdvak, en een vlak
 * ertussen suggereert een verloop tussen twee maanden dat er niet is.
 */
export function VerdelingKaart({ data }: { data: OverzichtData }) {
  const { reeksen, punten } = data.verdeling;

  const config: ChartConfig = Object.fromEntries(
    reeksen.map((r, i) => [r.id, { label: r.label, color: `var(--chart-${i + 1})` }]),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Hoofdonderwerpen per {data.periode.soort === 'maand' ? 'dag' : 'maand'}
        </CardTitle>
        <CardDescription>
          {reeksen.length > 0
            ? `${data.periode.label} · dezelfde ${reeksen.length} onderwerpen als hieronder`
            : data.periode.label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {reeksen.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Niets gevonden in deze periode</EmptyTitle>
              <EmptyDescription>
                Zodra er gesprekken zijn verwerkt verschijnt hier wat er per tijdvak is gezegd.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <ChartContainer className="h-[280px] w-full" config={config}>
            <BarChart accessibilityLayer data={punten} margin={{ left: 12, right: 12 }}>
              <CartesianGrid vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="tijdvak"
                minTickGap={16}
                tickFormatter={asTijdvakLabel}
                tickLine={false}
                tickMargin={8}
              />
              <ChartTooltip
                content={<ChartTooltipContent className="min-w-52" indicator="dot" />}
                cursor={false}
              />
              {reeksen.map((r) => (
                <Bar
                  dataKey={r.id}
                  fill={`var(--color-${r.id})`}
                  // Zelfde behandeling als de vlakken hieronder: doorschijnende
                  // vulling met een volle rand.
                  fillOpacity={0.4}
                  key={r.id}
                  stackId="a"
                  stroke={`var(--color-${r.id})`}
                  strokeWidth={1.5}
                />
              ))}
            </BarChart>
          </ChartContainer>
        )}
        <Legenda reeksen={reeksen} />
      </CardContent>
    </Card>
  );
}
